import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import i18n from 'i18next';
import { Eye, EyeSlash } from 'common/images/FaIcons';
import Checkbox from './Checkbox';
import RadioButton from '../molecules/Radiobutton';

export const Prefix = styled.div`
  padding: 0 ${props => (props.compactPrefix ? '0.5em' : '1em')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.greyXD};
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props =>
    props.borderColor || props.theme.colors.borderGrayHover};
  ${props =>
    props.prefixSide === 'left'
      ? `
    border-bottom-left-radius: ${props.theme.utils.borderRadius};
    border-top-left-radius: ${props.theme.utils.borderRadius};
    border-right: none;
    order: 1;
  `
      : `
    border-bottom-right-radius: ${props.theme.utils.borderRadius};
    border-top-right-radius: ${props.theme.utils.borderRadius};
    border-left: none;
    order: 2;
  `}
  ${props =>
    props.error
      ? `
    border-color: ${props.theme.colors.error};
  `
      : ''}
  ${props =>
    props.searchBox &&
    !props.value &&
    `
    border-color: transparent;
  `}
  ${props =>
    props.disabled &&
    `
    background-color: ${props.theme.colors.disabled};
    border: 0px;
  `}
  ${props => props.clickable && 'cursor: pointer;'}
`;

export const InputStyles = css`
  ${props =>
    props.fontSettings ? props.fontSettings : props.theme.fonts.input}
  margin: 0;
  outline: 0;
  width: 100%;
  flex-grow: 1;
  font-weight: 300;
  height: auto !important;
  line-height: ${props => props.theme.utils.inputLineHeight};
  padding: ${props => props.theme.utils.inputPadding};
  border: 1px solid ${props =>
    props.borderColor || props.theme.colors.borderGrayHover};
  color: ${props => props.theme.colors.black};
  background-color:${props => props.backgroundColor || props.theme.colors.white}
  border-radius: ${props => props.theme.utils.borderRadius};
  /* transition: box-shadow .1s ease,border-color .1s ease; */
  ${props =>
    !props.searchBox &&
    `
    &:hover:not(:disabled) {
      border-color: ${props => props.theme.primaryColor};
      + ${Prefix} {
        border-color: ${props => props.theme.primaryColor};
      }
    }
  `}
  &:active:not(:disabled), &:focus:not(:disabled) {
    border: 1px solid ${props => props.theme.primaryColor};
    + ${Prefix} {
      border-color: ${props => props.theme.primaryColor};
    }
  }
  :invalid {
    box-shadow: none;
  }
  &:disabled {
    background-color: ${props => props.theme.colors.disabled};
    color: ${props => props.theme.colors.greyXD};
    opacity: 1;
    border-color: ${props => props.theme.colors.disabled};
  }
  &::placeholder {
    opacity: .7;
    font-size: 13px;
  }
  ${props =>
    props.prefix
      ? `
    ${props.prefixSide === 'left' ? `
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
      border-left: none!important;
      order: 2;
    `
    : `
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
      border-right: none;
      order: 1;
    `
}
  `
      : ''}
  ${props =>
    props.compactPrefix &&
    props.prefix &&
    props.prefixSide === 'left' &&
    `
    padding-left: 0.35em;
  `}
  ${props =>
    props.compactPrefix &&
    props.prefix &&
    props.prefixSide === 'right' &&
    `
    padding-right: 0.35em;
  `}
  ${props =>
    props.error
      ? `
    border-color: ${props.theme.colors.error};
    color: ${props.theme.colors.error};
    &:hover:not(:disabled) {
      color: ${props.theme.colors.black};
    }
  `
      : ''};
  -moz-appearance: textfield;
  ::-webkit-inner-spin-button,
  ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
  }
  ${props =>
    props.searchBox &&
    `
    -webkit-transition: max-width .4s ease;
    -moz-transition: max-width .4s ease;
    -ms-transition: max-width .4s ease;
    -o-transition: max-width .4s ease;
    transition: max-width .4s ease;
    padding-left: 0;
    padding-right: 0;
    max-width: ${props.value ? '250px' : '0px'};
    ${props.theme.media.giant`
      width: ${props.value ? props.giantWidth || '250px' : '0'};
      max-width: ${props.value ? props.giantWidth || '250px' : '0'};
    `}
    ${props.theme.media.phone`
      width: ${props.value ? props.mobileWidth || '100px' : '0'};
      max-width: ${props.value ? props.mobileWidth || '100px' : '0'};
    `}
    ${props.theme.media.tablet`
      width: ${props.value ? props.tabletWidth || '180px' : '0'};
      max-width: ${props.value ? props.tabletWidth || '180px' : '0'};
    `}
    ${props.theme.media.desktop`
      width: ${props.value ? props.desktopWidth || '250px' : '0'};
      max-width: ${props.value ? props.desktopWidth || '250px' : '0'};
    `}
    border-color: ${props.value ? props.theme.colors.borderGrayHover : 'transparent'};
    padding-right: ${props.theme.utils.inputLineHeight};
    &:focus:not(:disabled) {
      -webkit-transition: max-width .4s ease;
      -moz-transition: max-width .4s ease;
      -ms-transition: max-width .4s ease;
      -o-transition: max-width .4s ease;
      transition: max-width .4s ease;
      max-width: 250px;
      ${props.theme.media.phone`
        width: ${props.value ? props.mobileWidth || '100px' : '0'};
        max-width: ${props.value ? props.mobileWidth || '100px' : '0'};
      `}
      ${props.theme.media.tablet`
        width: ${props.value ? props.tabletWidth || '180px' : '0'};
        max-width: ${props.value ? props.tabletWidth || '180px' : '0'};
      `}
      ${props.theme.media.desktop`
        width: ${props.value ? props.desktopWidth || '250px' : '0'};
        max-width: ${props.value ? props.desktopWidth || '250px' : '0'};
      `}
    }
  `}
`;

const Button = styled.button`
  border: none;
  background-color: transparent;
  outline: 0;
  padding: 0;
  cursor: default;
  &:after {
    content: "X";
    width: 16px;
    height: 16px;
    position: absolute;
    z-index: 1;
    right: 5px;
    top: 0;
    bottom: 0;
    margin: auto;
    color: #999b95;
    font-size: 14px;
    cursor: pointer;
  }
  ${props => !props.value && 'display: none;'}
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
`;

const InputElement = styled.input`
  ${InputStyles}
`;

const Input = ({
  prefix,
  prefixSide,
  value,
  error,
  clearable,
  clearInput,
  selectable,
  onClickSelectable,
  selectableValue,
  width,
  fontWeight,
  type,
  showPasswordToggle,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = React.createRef();
  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };

  const togglePasswordOnKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      togglePasswordVisibility();
    }
  };

  // the eye sits in a Prefix box, so the input must flatten its right edge like any prefixed input.
  // a caller's own prefix already owns that edge, and clearable's X is absolutely positioned over it,
  // so the toggle stands down rather than render two boxes on top of each other.
  const passwordToggle = type === 'password' && showPasswordToggle && !prefix && !clearable;
  const inputPrefix = prefix || (passwordToggle ? 'password-toggle' : undefined);
  const inputPrefixSide = prefix ? prefixSide : 'right';

  return (
    <Container
      className={rest.classNameProp}
      onClick={rest.searchBox ? focusInput : () => { }}
    >
      {selectable === 'checkbox' && (
        <Checkbox
          inputField
          onClick={onClickSelectable}
          value={selectableValue}
        />
      )}
      {selectable === 'radio' && (
        <RadioButton
          inputField
          onClick={onClickSelectable}
          value={selectableValue}
        />
      )}
      <InputElement
        innerRef={inputRef}
        autoFocus={rest.autoFocus}
        prefix={inputPrefix}
        prefixSide={inputPrefixSide}
        compactPrefix={rest.compactPrefix}
        value={value}
        error={error}
        type={!showPassword ? type : 'text'}
        {...rest}
        {...(type === 'password' ? {
          // revealed, the field is a plain text input, which browsers spell-check and
          // mobile keyboards autocorrect and learn from. masked inputs are exempt; this one is not.
          spellCheck: false,
          autoCorrect: 'off',
          autoCapitalize: 'none'
        } : {})}
      />
      {prefix && (
        <Prefix
          disabled={rest.disabled}
          value={value}
          searchBox={rest.searchBox}
          prefixSide={prefixSide}
          compactPrefix={rest.compactPrefix}
          error={error}
          borderColor={rest.borderColor}
          width={width}
        >
          {prefix}
        </Prefix>
      )}
      {passwordToggle && (
        <Prefix
          role="button"
          tabIndex={0}
          aria-label={i18n.t(showPassword ? 'common.hidePassword' : 'common.showPassword')}
          title={i18n.t(showPassword ? 'common.hidePassword' : 'common.showPassword')}
          onClick={togglePasswordVisibility}
          onKeyDown={togglePasswordOnKey}
          clickable
          disabled={rest.disabled}
          error={error}
          borderColor={rest.borderColor}
          prefixSide="right"
        >
          {showPassword ? <EyeSlash /> : <Eye />}
        </Prefix>
      )}
      {clearable && <Button onClick={clearInput} value={value} />}
    </Container>
  );
};

Input.propTypes = {
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  prefixSide: PropTypes.oneOf(['left', 'right']),
  onClickSelectable: PropTypes.func,
  selectableValue: PropTypes.bool,
  selectable: PropTypes.oneOf(['radio', 'checkbox']),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.number
  ]),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  // on by default for type="password"; ignored when `prefix` or `clearable` owns the right edge
  showPasswordToggle: PropTypes.bool,
  compactPrefix: PropTypes.bool
};

Input.defaultProps = {
  prefix: undefined,
  prefixSide: 'right',
  selectable: undefined,
  onClickSelectable: () => { },
  selectableValue: false,
  value: undefined,
  error: undefined,
  clearable: false,
  disabled: false,
  showPasswordToggle: true,
  compactPrefix: false
};

Input.displayName = 'Input';

export default styled(Input)``;
